import {L} from "../landcare-toolkit";
import {IToastService} from "../../services/ToastService";
import {IUsageLoggingService} from "../../services/IUsageLoggingService";
import {Options as ControlOptions} from 'ol/control/Control';
import {Coordinate} from 'ol/coordinate';
import Feature from "ol/Feature";
import Fill from "ol/style/Fill";
import {Circle, Stroke, Style} from "ol/style";
import {Control} from "ol/control";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {Geometry, Point} from "ol/geom";
import {transform} from "ol/proj";
import {containsCoordinate} from "ol/extent";

export class PositionFeature extends Feature<Geometry> {
	constructor(colour: string = '#3399CC') {
		super();

		const baseStyle = new Style({
			image: new Circle({
				radius: 6,
				fill: new Fill({
					color: colour
				}),
				stroke: new Stroke({
					color: '#FFFFFF',
					width: 2
				})
			})
		})

		this.setStyle((f, resolution) => {
			const accuracy = (this.get('accuracy') || 0) / (Math.max(resolution, 1) * 2);

			return accuracy
				? [
					baseStyle,
					new Style({
						image: new Circle({
							radius: accuracy,
							fill: new Fill({
								color: '#FFFFFF44'
							})
						})
					})
				]
				: baseStyle;
		});
	}
}

enum LocateState {
	Off,
	Searching,
	Found
}

export class LocateMeControl extends Control {
	constructor(protected geolocation: Geolocation,
				protected usageLoggingService : IUsageLoggingService = null,
				protected toastService : IToastService = null,
				protected element_: HTMLElement = document.createElement("button"),
				protected options: ControlOptions = {}) {
		super({
			element: element_
		});

		element_.className = 'material-floating-button locate-me ol-control';
		element_.setAttribute('role','button');
		// TODO: add long press detection for turning off the location service.
		element_.addEventListener('click', () => this.onClick());
		element_.addEventListener('contextmenu', (e) => this.cancelListener(e));
		element_.setAttribute('title', 'Locate Me');
	}


	protected state: LocateState = LocateState.Off;
	protected onClick() {

		let action = '';
		switch (this.state) {
			case LocateState.Off:
				action = 'requestLocation';
				this.requestLocation();
				break;
			case LocateState.Found:
				action = 'centreOnLastKnownLocation';
				this.centreOnLastKnownLocation();
				break;
			case LocateState.Searching:
				break;
		}
		if (action && this.usageLoggingService) this.usageLoggingService.logWithAnalytics('locateMe', 'button', 'action');
	}

	public doGeoLocate() {
		if (this.state == LocateState.Off) {
			this.requestLocation();
		}
	}

	protected toggleGpsButtonBackground(gpsFixedOrNot: boolean) {
		this.element_.classList.toggle('gps-fixed', gpsFixedOrNot);
	}

	protected lastTimeStamp = 0;
	protected runningTotalMillis: number = 0;
	protected gpsIconState: boolean = false;

	animateButton: FrameRequestCallback = (elapsedMillis) => {
		const interval = 500;
		this.runningTotalMillis += elapsedMillis - this.lastTimeStamp;
		this.lastTimeStamp = elapsedMillis;

		switch (this.state) {
			case LocateState.Searching: {
				if (this.runningTotalMillis > interval) {
					this.gpsIconState = !this.gpsIconState;
					this.toggleGpsButtonBackground(this.gpsIconState);
					this.runningTotalMillis = 0;
				}
				requestAnimationFrame((count) => this.animateButton(count));
				break;
			}
			case LocateState.Found:
				this.toggleGpsButtonBackground(true);
				break;
			case LocateState.Off:
				this.toggleGpsButtonBackground(false);
				break;
		}
	};

	protected watchHandle: number = null;

	protected requestLocation() {
		if (this.state != LocateState.Searching) {
			this.state = LocateState.Searching;
			this.lastTimeStamp = performance.now();

			window.requestAnimationFrame((elapsedMillis) => {this.animateButton(elapsedMillis)});

			this.watchHandle = this.geolocation.watchPosition(
				(position) => this.updatePosition(position),
				(positionError) => {
					this.onPositionError(positionError);
				}
			)
		}
	}

	protected lastKnownPosition = null;

	protected updatePosition(position: Position) {
		let first = !this.lastKnownPosition;
		this.lastKnownPosition = position;
		this.state = LocateState.Found;
		if (first) {
			this.centreOnLastKnownLocation();
		}

		this.updateMyLocationFeature();
	}

	protected onPositionError(positionError: PositionError) {
		let prefix = "Error(" + positionError.code + ")";
		console.warn(positionError.message);
		switch (positionError.code) {
			case positionError.PERMISSION_DENIED:
				prefix = "Location permission denied, please check your browser/device's location settings.";
				break;
			case positionError.POSITION_UNAVAILABLE:
				prefix = "Location service unavailable, please check your browser/device's location settings.";
				break;
		}

		alert(prefix + " Message:" + positionError.message);

		this.state = LocateState.Off;
	}

	protected positionFeature: PositionFeature = null;
	protected _positionLayer: VectorLayer<VectorSource<Geometry>> = null;

	private getPositionLayer() {
		if (this._positionLayer == null) {
			this._positionLayer = new VectorLayer(<any> {
				zIndex: L.util.Layer.zIndexForLayerType(L.layer.Type.CONTEXT, 888) + 1,
				updateWhileAnimating: true
			});
			this.getMap().addLayer(this._positionLayer);
			this.getPositionLayer().setSource(new VectorSource({

			}))
		}
		return this._positionLayer;
	}

	private updateMyLocationFeature() {
		let transformedCoord = this.getCurrentPositionTransformedToMap();

        if (this.positionFeature == null) {
            this.positionFeature = new PositionFeature();
            let source: any = this.getPositionLayer().getSource();
            source.addFeature(this.positionFeature);
        }

		const position = this.lastKnownPosition as Position;

		this.positionFeature.set('accuracy', position && position.coords && position.coords.accuracy || 0);
        this.positionFeature.setGeometry(new Point(transformedCoord));
	}

	private getCurrentPositionTransformedToMap() : Coordinate {
        let position = this.lastKnownPosition;
        let coord: Coordinate = [position.coords.longitude, position.coords.latitude];
        let proj: any = this.getMap().getView().getProjection();
        return transform(coord, "EPSG:4326", proj);
	}

	private centreOnLastKnownLocation() {
		let transformed = this.getCurrentPositionTransformedToMap();
		let view = this.getMap().getView();

		if (!containsCoordinate(L.util.Map.defaultExtent(), transformed)) {
			this.toastService.showToast("Locate functionality only available for use within NZ.");
			return;
		}

		view.setCenter(transformed);
		view.setZoom(9);

	}

	private cancelListener(e: Event) {
		e.preventDefault();
		e.stopPropagation();
		try {
			this.toggleGpsButtonBackground(false);
			this.geolocation.clearWatch(this.watchHandle);
			this.lastKnownPosition = null;
			this.state = LocateState.Off;
		} catch (e) {

		}
		return false;
	}
}